import { useBlocksFromTimestamps } from 'hooks/useBlocksFromTimestamps'
import { useState, useEffect, useMemo } from 'react'
import { useDeltaTimestamps } from 'utils/thegraph'
import { useWeb3React } from '@web3-react/core'
import { SupportedChainId } from 'constants/chains'
import { apolloClient } from 'graphql/thegraph/apollo'
import gql from 'graphql-tag'
import { START_BLOCKS } from 'constants/blocks'

export interface EthPrices {
  current: number
  oneDay: number
  twoDay: number
}

export const ETH_PRICES = gql`
  query prices($block24: Int!, $block48: Int!) {
    current: bundles(first: 1, subgraphError: allow) {
      ethPriceUSD
    }
    oneDay: bundles(first: 1, block: { number: $block24 }, subgraphError: allow) {
      ethPriceUSD
    }
    twoDay: bundles(first: 1, block: { number: $block48 }, subgraphError: allow) {
      ethPriceUSD
    }
  }
`

interface PricesResponse {
  current: {
    ethPriceUSD: string
  }[]
  oneDay: {
    ethPriceUSD: string
  }[]
  twoDay: {
    ethPriceUSD: string
  }[]
}

async function fetchEthPrices(
  blocks: [number, number, number]
): Promise<{ data: EthPrices | undefined; error: boolean }> {
  try {
    const { data, error } = await apolloClient.query<PricesResponse>({
      query: ETH_PRICES,
      variables: {
        block24: blocks[0] ?? START_BLOCKS[SupportedChainId.XDC],
        block48: blocks[1] ?? START_BLOCKS[SupportedChainId.XDC],
        blockWeek: blocks[2] ?? START_BLOCKS[SupportedChainId.XDC],
      },
    })
    if (error) {
      return {
        error: true,
        data: undefined,
      }
    } else if (data) {
      return {
        data: {
          current: parseFloat(data.current[0].ethPriceUSD ?? 0),
          oneDay: parseFloat(data.oneDay[0]?.ethPriceUSD ?? 0),
          twoDay: parseFloat(data.twoDay[0]?.ethPriceUSD ?? 0),
        },
        error: false,
      }
    } else {
      return {
        data: undefined,
        error: true,
      }
    }
  } catch (e) {
    return {
      data: undefined,
      error: true,
    }
  }
}

/**
 * returns eth prices at current, 24h, 48h, and 1w intervals
 */
export function useEthPrices(): EthPrices | undefined {
  const [prices, setPrices] = useState<{ [network: string]: EthPrices | undefined }>()
  const [error, setError] = useState(false)

  const [t24, t48] = useDeltaTimestamps()
  const { blocks, error: blockError } = useBlocksFromTimestamps([t24, t48])

  // index on active network
  const { chainId } = useWeb3React()
  const indexedPrices = prices?.[chainId as SupportedChainId]

  const formattedBlocks = useMemo(() => {
    if (blocks) {
      return blocks.map((b) => parseFloat(b.number))
    }
    return undefined
  }, [blocks])

  useEffect(() => {
    async function fetch() {
      const { data, error } = await fetchEthPrices(formattedBlocks as [number, number, number])
      if (error || blockError) {
        setError(true)
      } else if (data) {
        setPrices({
          [chainId as SupportedChainId]: data,
        })
      }
    }
    if (!indexedPrices && !error && formattedBlocks) {
      fetch()
    }
  }, [error, prices, formattedBlocks, blockError, apolloClient, indexedPrices, chainId])

  return prices?.[chainId as SupportedChainId]
}
